import { Plugin, PluginKey } from 'hubspot-prosemirror/pm/state';
import { EditorView } from 'hubspot-prosemirror/pm/view';
import { setTrSelectionPosition } from '../utils/documentUtils';

type FocusPluginState = {
  focusView?: boolean;
  focusViewPos?: number;
  preserveSelection?: boolean;
};

type FocusEditorViewOptions = FocusPluginState & {
  // Set only on the mount-autofocus path, so a passively-mounted editor does
  // not pull focus from one the user is actively typing in.
  respectExistingFocus?: boolean;
};

const focusPluginKey = new PluginKey<FocusPluginState>('FocusPlugin');

function isExternalEditableFocused(
  view: EditorView,
  includeContentEditable: boolean
): boolean {
  // A root's `activeElement` only reports focus within its own tree, so descend
  // through any shadow roots to the element that actually holds focus.
  let active: Element | null = view.dom.ownerDocument.activeElement;
  while (active?.shadowRoot?.activeElement) {
    active = active.shadowRoot.activeElement;
  }
  if (!active || view.dom.contains(active)) {
    return false;
  }
  const { tagName } = active;
  if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
    return true;
  }
  return (
    includeContentEditable && (active as HTMLElement).isContentEditable === true
  );
}

export const FocusPlugin = new Plugin<FocusPluginState>({
  key: focusPluginKey,
  state: {
    init() {
      return {};
    },
    apply(tr, currentPluginState) {
      const meta = tr.getMeta(FocusPlugin);
      if (meta) {
        return meta;
      }
      return currentPluginState;
    },
  },
  view() {
    return {
      update: (view: EditorView) => {
        const pluginState = focusPluginKey.getState(view.state);
        if (pluginState && pluginState.focusView) {
          // only focus if it isn't currently focused
          window.requestAnimationFrame(() => {
            if (!view.dom.isConnected) {
              return;
            }
            const state = focusPluginKey.getState(view.state);
            if (!state || !state.focusView) {
              return;
            }
            focusEditorView(view, {
              focusViewPos: state.focusViewPos,
              preserveSelection: state.preserveSelection,
            });
          });
        }
      },
    };
  },
});

export function focusEditorView(
  view: EditorView,
  {
    focusViewPos,
    preserveSelection,
    respectExistingFocus,
  }: FocusEditorViewOptions = {}
): void {
  // Acting on a view that unmounted before this frame ran throws.
  if (!view.dom.isConnected) {
    return;
  }
  if (isExternalEditableFocused(view, Boolean(respectExistingFocus))) {
    // Focus is held elsewhere; don't steal it. Clear any armed intent.
    if (focusPluginKey.getState(view.state)?.focusView) {
      view.dispatch(view.state.tr.setMeta(FocusPlugin, {}));
    }
    return;
  }
  if (!preserveSelection && view.hasFocus()) {
    return;
  }
  if (!view.hasFocus()) {
    view.focus();
  }
  const tr = preserveSelection
    ? view.state.tr
    : setTrSelectionPosition(view.state.tr, focusViewPos);
  view.dispatch(tr.setMeta(FocusPlugin, {}));
}
